import {
    ExpositionState,
    ParticipantState,
    TimeSync
} from "../../../shared/interfaces";

const EXPOSITION_STATE_HISTORY_LENGTH: number = 25;

const DEFAULT_EXPOSITION_STATE: ExpositionState = {
    serverTime: 0,
    requirePing: false,
    participants: {}
};



class SynchronizedExpositionState {

    enabled: boolean;
    renderDelay: number;
    timeSync: TimeSync;
    latestExpositionStates: ExpositionState[] = []

    constructor() {
        this.enabled = false; // will be turned on via message from backend
        this.renderDelay = 50;  // [ms] (must be higher for smaller server tick rates)
    }

    updateTimeSync(timeSync: TimeSync) {
        this.timeSync = timeSync;
    }

    insertState(state: ExpositionState) {
        if(this.latestExpositionStates.length >= EXPOSITION_STATE_HISTORY_LENGTH-1) {
            this.latestExpositionStates.shift();
        }
        this.latestExpositionStates.push(state);
    }

    getInterpolatedState(): ExpositionState {
        if(this.latestExpositionStates.length < 1) {
            return DEFAULT_EXPOSITION_STATE;
        }

        if(!this.enabled) {
            return this.latestExpositionStates[this.latestExpositionStates.length-1];
        }

        const currentServerTime = this.estimateCurrentServerTime();
        const renderedServerTime = currentServerTime - this.renderDelay;

        // find state before and after current server time (should be consecutive numbers)
        let beforeIndex = -1;
        let afterIndex = -1;

        this.latestExpositionStates.forEach((state, idx) => {
            if(state.serverTime < renderedServerTime) {
                if(beforeIndex < 0 || state.serverTime > this.latestExpositionStates[beforeIndex].serverTime) {
                    beforeIndex = idx;
                }
            }
            if(state.serverTime > renderedServerTime) {
                if(afterIndex < 0 || state.serverTime < this.latestExpositionStates[afterIndex].serverTime) {
                    afterIndex = idx;
                }
            }
        })

        //console.log('before/after Index', beforeIndex, afterIndex)
        //console.log('renderedServerTime', renderedServerTime)
        //console.log(this.latestExpositionStates[0].serverTime, this.latestExpositionStates[this.latestExpositionStates.length-1].serverTime)

        const beforeState = beforeIndex > 0 ? this.latestExpositionStates[beforeIndex] : undefined;
        const afterState = afterIndex > 0 ? this.latestExpositionStates[afterIndex] : undefined;
        const interpolatedState: ExpositionState = this.interpolateState(beforeState, afterState, renderedServerTime);

        return interpolatedState;
    }

    interpolateState(before: ExpositionState | undefined, after: ExpositionState | undefined, currentTime: number): ExpositionState {

        if(!before) {
            console.log("ERROR: no <before> state available")
            return DEFAULT_EXPOSITION_STATE;
        }

        if(!after) {
            console.log("WARNING: no <after> state available")
            return before; // just return latest state here. we don't do any prediction.
        }

        // calculate linear interpolation factors
        const totalDelta = after.serverTime - before.serverTime;
        const currentDelta = currentTime - before.serverTime;
        const beforeFactor = currentDelta / totalDelta;
        const afterFactor = 1.0 - beforeFactor;

        const interpolatedParticipants: {
            [participantId: string]: ParticipantState
        } = {};

        Object.keys(after.participants).forEach(participantId => {
            if(before.hasOwnProperty(participantId)) {
                // participant in both states present. Interpolation required.
                const participantBefore = before.participants[participantId];
                const participantAfter = after.participants[participantId];
                const interpolatedParticipant: ParticipantState = {
                    name: participantAfter.name, // strings cannot be interpolated
                    ping: participantAfter.ping, // no interpolation required
                    position: participantBefore.position.scale(beforeFactor).add( participantAfter.position.scale(afterFactor) ),
                    direction: participantBefore.direction.scale(beforeFactor).add( participantAfter.direction.scale(afterFactor) ),
                    isWalking: afterFactor > 0.5 ? participantAfter.isWalking : participantBefore.isWalking,
                    colors: participantAfter.colors // no interpolation required
                };
                interpolatedParticipants[participantId] = interpolatedParticipant;
            } else {
                // participant only in 'after' state present. No interpolation required, just take it as is
                interpolatedParticipants[participantId] = after.participants[participantId];
            }
        })

        const interpolatedState: ExpositionState = {
            serverTime: currentTime, // currentTime is interpolated server time
            requirePing: false, // no interpolation required
            participants: interpolatedParticipants
        }

        return interpolatedState;
    }

    estimateCurrentServerTime() {
        const currentTime = new Date().getTime();
        const timeElapsed = currentTime - this.timeSync.clientTime; // time elapsed since last timeSync
        const currentServerTime = this.timeSync.serverTime + timeElapsed;
        return currentServerTime;
    }

    setEnabled(enabled: boolean) {
        if(enabled !== this.enabled) {
            this.enabled = enabled;
            console.log('INTERPOLATION_ENABLED', this.enabled);
        }
    }

    setTickRate(tickRateHz: number) {
        const oldRenderDelay = this.renderDelay;
        const tickLengthMs = 1000 / tickRateHz;
        const newRenderDelay = Math.round(1.5 * tickLengthMs);
        this.renderDelay = newRenderDelay;
        console.log('UPDATE RENDER DELAY', `${this.renderDelay}ms`, `(was ${oldRenderDelay}ms)`);
    }
}

export default new SynchronizedExpositionState();
